import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Package,
  Calendar,
  Eye,
  FileText,
  ArrowLeft,
  ArrowRight,
  Truck,
  CreditCard,
  ClipboardList,
} from "lucide-react";
import api from "../../api/client";
import { useLanguage } from "../../contexts/LanguageContext";
import { t } from "../../i18n";
import logger from "../../utils/logger.js";
import Breadcrumb from "../ui/Breadcrumb/Breadcrumb";
import Pagination from "../ui/Pagination/Pagination";
import useBurgerMenu from "../../hooks/useBurgerMenu";
import "./MyOrders.css";

const ORDERS_PER_PAGE = 9;

function useTranslation(lang) {
  return (key, arFallback, enFallback) => {
    const translated = t(lang, key);
    if (translated && translated !== key) return translated;
    return lang === "ar" ? arFallback : enFallback;
  };
}

function formatDate(dateString, lang) {
  if (!dateString) return "—";
  try {
    const date = new Date(dateString);
    if (lang === "ar") {
      return date.toLocaleDateString("ar-SA", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateString;
  }
}

function getStatusClass(status) {
  const s = (status || "").toLowerCase();
  if (s === "pending") return "badge-pending";
  if (s === "processing") return "badge-processing";
  if (s === "completed") return "badge-completed";
  if (s === "canceled" || s === "cancelled") return "badge-canceled";
  return "badge-default";
}

function getStatusLabel(status, isAr) {
  const s = (status || "").toLowerCase();
  const map = {
    pending: { ar: "قيد الانتظار", en: "Pending" },
    processing: { ar: "قيد التجهيز", en: "Processing" },
    completed: { ar: "مكتمل", en: "Completed" },
    canceled: { ar: "ملغي", en: "Canceled" },
    cancelled: { ar: "ملغي", en: "Canceled" },
  };
  return map[s]?.[isAr ? "ar" : "en"] || status || "—";
}

function getPaymentClass(paymentStatus) {
  const s = (paymentStatus || "").toLowerCase();
  if (s === "paid") return "badge-paid";
  if (s === "failed") return "badge-failed";
  if (s === "cod") return "badge-cod";
  if (s === "unpaid") return "badge-unpaid";
  return "badge-default";
}

function getPaymentLabel(paymentStatus, isAr) {
  const s = (paymentStatus || "").toLowerCase();
  const map = {
    paid: { ar: "مدفوع", en: "Paid" },
    pending: { ar: "قيد المعالجة", en: "Pending" },
    failed: { ar: "فشل الدفع", en: "Failed" },
    cod: { ar: "دفع عند الاستلام", en: "Cash on delivery" },
    unpaid: { ar: "غير مدفوع", en: "Unpaid" },
  };
  return map[s]?.[isAr ? "ar" : "en"] || paymentStatus || "—";
}

function getPaymentMethodLabel(method, isAr) {
  const m = (method || "").toLowerCase();
  if (!m) return "—";
  if (m === "cod") return isAr ? "الدفع عند الاستلام" : "Cash on delivery";
  if (["card", "stripe_card", "stripe", "test_card"].includes(m))
    return isAr ? "بطاقة (Stripe)" : "Card (Stripe)";
  if (m === "paypal") return "PayPal";
  if (m === "bank_transfer") return isAr ? "تحويل بنكي" : "Bank transfer";
  if (m === "wallet") return isAr ? "محفظة إلكترونية" : "Wallet";
  return method;
}

function getShippingClass(status) {
  const s = (status || "").toLowerCase();
  if (s === "shipped") return "badge-shipped";
  if (s === "in_transit") return "badge-transit";
  if (s === "delivered") return "badge-delivered";
  if (s === "pending") return "badge-pending";
  if (s === "processing") return "badge-processing";
  if (s === "canceled" || s === "cancelled") return "badge-canceled";
  return "badge-default";
}

function getShippingLabel(status, isAr) {
  const s = (status || "").toLowerCase();
  const map = {
    pending: { ar: "قيد الانتظار", en: "Pending" },
    processing: { ar: "قيد التجهيز", en: "Processing" },
    shipped: { ar: "تم الشحن", en: "Shipped" },
    in_transit: { ar: "في الطريق", en: "In transit" },
    delivered: { ar: "تم التسليم", en: "Delivered" },
    canceled: { ar: "ملغي", en: "Canceled" },
    cancelled: { ar: "ملغي", en: "Canceled" },
  };
  return map[s]?.[isAr ? "ar" : "en"] || status || (isAr ? "غير متوفر" : "N/A");
}

function LoadingShimmer({ count = 6 }) {
  return (
    <div className="orders-loading">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="shimmer-card">
          <div className="shimmer-bar" />
          <div style={{ padding: "18px 20px 12px" }}>
            <div
              className="shimmer-block"
              style={{ width: "40%", height: "14px", marginBottom: "8px" }}
            />
            <div
              className="shimmer-block"
              style={{ width: "60%", height: "22px" }}
            />
          </div>
          <div
            style={{
              padding: "0 20px 16px",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            <div
              className="shimmer-block"
              style={{
                width: "100%",
                height: "50px",
                borderRadius: "var(--radius-md)",
              }}
            />
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "10px",
              }}
            >
              <div
                className="shimmer-block"
                style={{ height: "50px", borderRadius: "var(--radius-md)" }}
              />
              <div
                className="shimmer-block"
                style={{ height: "50px", borderRadius: "var(--radius-md)" }}
              />
              <div
                className="shimmer-block"
                style={{ height: "50px", borderRadius: "var(--radius-md)" }}
              />
              <div
                className="shimmer-block"
                style={{ height: "50px", borderRadius: "var(--radius-md)" }}
              />
            </div>
          </div>
          <div
            style={{
              padding: "14px 20px 18px",
              borderTop: "1px solid var(--border-color)",
              display: "flex",
              gap: "10px",
            }}
          >
            <div
              className="shimmer-block"
              style={{ flex: 1, height: "38px", borderRadius: "var(--radius-md)" }}
            />
            <div
              className="shimmer-block"
              style={{
                width: "38px",
                height: "38px",
                borderRadius: "var(--radius-md)",
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function OrderCard({ order, invoice, isAr, isRTL, labels }) {
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

  return (
    <article className="order-card">
      <div className="order-card-header">
        <div className="order-card-id-wrap">
          <span className="order-card-label">
            {isAr ? "رقم الطلب" : "Order"}
          </span>
          <span className="order-card-id" dir="ltr">
            #{String(order.id).split(".")[0]}
          </span>
        </div>
        <span className="order-card-date">
          <Calendar size={13} />
          {formatDate(order.created_at, isAr ? "ar" : "en")}
        </span>
      </div>

      <div className="order-card-body">
        <div className="order-card-total-row">
          <span className="order-card-total-label">{labels.total}</span>
          <span className="order-card-total-value">
            {Number(order.total || 0).toFixed(2)}
            <span className="currency">$</span>
          </span>
        </div>

        <div className="order-card-status-grid">
          <div className="order-card-status-item">
            <span className="order-card-status-label">
              <ClipboardList
                size={11}
                style={{ marginInlineEnd: "4px", verticalAlign: "-1px" }}
              />
              {labels.status}
            </span>
            <span className={`order-badge ${getStatusClass(order.status)}`}>
              {getStatusLabel(order.status, isAr)}
            </span>
          </div>

          <div className="order-card-status-item">
            <span className="order-card-status-label">
              <CreditCard
                size={11}
                style={{ marginInlineEnd: "4px", verticalAlign: "-1px" }}
              />
              {labels.paymentStatus}
            </span>
            <span
              className={`order-badge ${getPaymentClass(order.payment_status)}`}
            >
              {getPaymentLabel(order.payment_status, isAr)}
            </span>
          </div>

          <div className="order-card-status-item">
            <span className="order-card-status-label">
              {labels.paymentMethod}
            </span>
            <span className="order-card-status-value">
              {getPaymentMethodLabel(order.payment_method, isAr)}
            </span>
          </div>

          <div className="order-card-status-item">
            <span className="order-card-status-label">
              <Truck
                size={11}
                style={{ marginInlineEnd: "4px", verticalAlign: "-1px" }}
              />
              {labels.shipping}
            </span>
            <span
              className={`order-badge ${getShippingClass(order.shipping_status)}`}
            >
              {getShippingLabel(order.shipping_status, isAr)}
            </span>
          </div>

          {order.tracking_code && (
            <div className="order-card-status-item full-width">
              <span className="order-card-status-label">{labels.tracking}</span>
              <span className="order-card-tracking-code" dir="ltr">
                {order.tracking_code}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="order-card-footer">
        <Link
          to={`/order/${order.id}`}
          className="order-btn order-btn-primary"
        >
          <Eye size={15} />
          <span>{labels.view}</span>
          <ArrowIcon size={13} strokeWidth={2.5} />
        </Link>

        {invoice && (
          <Link
            to={`/invoice/${invoice.id}`}
            className="order-btn order-btn-icon"
            title={labels.viewInvoice}
            aria-label={labels.viewInvoice}
          >
            <FileText size={16} />
          </Link>
        )}
      </div>
    </article>
  );
}

export default function MyOrders({ orders: propOrders, isAr: propIsAr, embedded }) {
  const [orders, setOrders] = useState(propOrders || []);
  const [loading, setLoading] = useState(!propOrders);
  const [invoicesMap, setInvoicesMap] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [visibleCount, setVisibleCount] = useState(ORDERS_PER_PAGE);
  const [isMobile, setIsMobile] = useState(false);

  const { lang } = useLanguage();
  const isAr = propIsAr ?? (lang === "ar");
  const isRTL = isAr;
  const tx = useTranslation(lang);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const handler = (e) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    if (propOrders && propOrders.length > 0) {
      setOrders(propOrders);
      setLoading(false);
      return;
    }
    if (embedded && propOrders) {
      setOrders(propOrders);
      setLoading(false);
      return;
    }

    async function loadOrders() {
      setLoading(true);
      try {
        const res = await api.apiGet("orders");
        if (Array.isArray(res?.data)) {
          setOrders(res.data);
        } else if (Array.isArray(res?.orders)) {
          setOrders(res.orders);
        } else {
          setOrders([]);
        }

        const invRes = await api.apiGet("invoices");
        if (invRes?.status === "success" && Array.isArray(invRes.invoices)) {
          const map = {};
          invRes.invoices.forEach((inv) => {
            if (inv.order_id != null) map[Number(inv.order_id)] = inv;
          });
          setInvoicesMap(map);
        } else if (Array.isArray(invRes?.data)) {
          const map = {};
          invRes.data.forEach((inv) => {
            if (inv.order_id != null) map[Number(inv.order_id)] = inv;
          });
          setInvoicesMap(map);
        } else {
          setInvoicesMap({});
        }
      } catch (e) {
        logger.error("MyOrders load error", e);
        setOrders([]);
        setInvoicesMap({});
      }
      setLoading(false);
    }

    if (!propOrders) loadOrders();
  }, [lang, propOrders, embedded]);

  const labels = {
    title: tx("my_orders", "طلباتي", "My Orders"),
    noOrders: tx("no_orders", "لا يوجد طلبات حتى الآن", "No orders yet."),
    total: tx("total", "الإجمالي", "Total"),
    status: tx("status", "حالة الطلب", "Status"),
    paymentStatus: tx("payment_status", "حالة الدفع", "Payment Status"),
    paymentMethod: tx("payment_method", "طريقة الدفع", "Payment Method"),
    shipping: tx("shipping", "الشحن", "Shipping"),
    tracking: tx("tracking", "التتبع", "Tracking"),
    date: tx("date", "التاريخ", "Date"),
    view: tx("view_details", "عرض التفاصيل", "View details"),
    viewInvoice: tx("view_invoice", "عرض الفاتورة", "View invoice"),
  };

  const totalPages = Math.max(1, Math.ceil(orders.length / ORDERS_PER_PAGE));
  const currentPageSafe = Math.min(currentPage, totalPages);

  const displayedOrders = useMemo(() => {
    if (isMobile) {
      return orders.slice(0, visibleCount);
    }
    const start = (currentPageSafe - 1) * ORDERS_PER_PAGE;
    return orders.slice(start, start + ORDERS_PER_PAGE);
  }, [orders, isMobile, visibleCount, currentPageSafe]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleLoadMore = useCallback(() => {
    setVisibleCount((prev) => prev + ORDERS_PER_PAGE);
  }, []);

  return (
    <div className={`orders-page ${embedded ? "orders-embedded" : ""}`} dir={isRTL ? "rtl" : "ltr"}>
      {!embedded && <Breadcrumb items={[{ label: labels.title }]} />}
      {!embedded && (
        <header className="orders-header">
          <div className="orders-header-content">
            <div className="orders-title-wrapper">
              <div className="orders-header-icon">
                <Package size={22} strokeWidth={2} />
              </div>
              <div className="orders-title-group">
                <h1 className="orders-header-title">{labels.title}</h1>
              </div>
            </div>
          </div>
        </header>
      )}

      <div className="orders-container">
        <div className="orders-content">
          {embedded && (
            <div className="orders-embedded-header">
              <div className="orders-embedded-icon">
                <Package size={20} strokeWidth={2} />
              </div>
              <h2 className="orders-embedded-title">{labels.title}</h2>
            </div>
          )}

          {loading ? (
            <LoadingShimmer count={embedded ? 4 : 6} />
          ) : orders.length === 0 ? (
            <div className="orders-empty">
              <div className="orders-empty-icon">
                <Package size={48} strokeWidth={1.5} />
              </div>
              <h2>{labels.title}</h2>
              <p>{labels.noOrders}</p>
            </div>
          ) : (
            <>
              <div className="orders-grid">
                {displayedOrders.map((o) => (
                  <OrderCard
                    key={o.id}
                    order={o}
                    invoice={invoicesMap[Number(o.id)]}
                    isAr={isAr}
                    isRTL={isRTL}
                    labels={labels}
                  />
                ))}
              </div>

              {!embedded && (
                <div className="pagination-desktop">
                  <Pagination
                    currentPage={currentPageSafe}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}

              {isMobile && orders.length > displayedOrders.length && (
                <div className="load-more-wrapper">
                  <button className="load-more-btn" onClick={handleLoadMore}>
                    {isAr ? "تحميل المزيد" : "Load More"}
                    <span className="load-more-count">
                      ({displayedOrders.length} / {orders.length})
                    </span>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}